import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const apple = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif";
const ORANGE = "#ff6b00";
const GREEN = "#10b981";
const BLUE = "#3b82f6";

/* ============================================================ */
/*  ENTRY                                                       */
/* ============================================================ */
export default function RYShipApp() {
  const [state, setState] = useState(() => {
    try {
      const saved = localStorage.getItem("ry-ship-state");
      return saved ? JSON.parse(saved) : { auth: "in" };
    } catch {
      return { auth: "in" };
    }
  });

  useEffect(() => {
    try { localStorage.setItem("ry-ship-state", JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  return (
    <div style={{ height: "100%", background: "#0a0a0a", color: "white", fontFamily: apple, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <AnimatePresence mode="wait">
        {state.auth === "in" && <MainApp key="main" onLogout={() => setState({ auth: "splash" })} />}
        {state.auth === "splash" && <Splash key="splash" onIn={() => setState({ auth: "in" })} />}
      </AnimatePresence>
    </div>
  );
}

function Splash({ onIn }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <div style={{ height: 54 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", padding: "0 32px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "radial-gradient(ellipse at 50% 30%, rgba(255,107,0,0.18), transparent 60%)" }} />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: 11, letterSpacing: 3, color: ORANGE, fontWeight: 700 }}>RY · SHIP</div>
          <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: -1.5, marginTop: 12, lineHeight: 1.02 }}>
            Quote. Book.<br/>Track. Done.
          </div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", marginTop: 16, lineHeight: 1.5, maxWidth: 280 }}>
            AI-priced freight booking for shippers. Instant quotes, live tracking, single invoice.
          </div>
        </div>
      </div>
      <div style={{ padding: "0 24px 36px" }}>
        <motion.button whileTap={{ scale: 0.97 }} onClick={onIn} style={{ background: ORANGE, color: "white", border: "none", padding: "14px 16px", borderRadius: 14, fontSize: 15, fontWeight: 700, fontFamily: apple, cursor: "pointer", width: "100%" }}>
          Sign in to RY Ship
        </motion.button>
      </div>
    </motion.div>
  );
}

/* ============================================================ */
/*  MAIN APP                                                    */
/* ============================================================ */
function MainApp({ onLogout }) {
  const [tab, setTab] = useState("home");
  const [toast, setToast] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [trackingLoad, setTrackingLoad] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2200);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div style={{ height: 54 }} />
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
        {tab === "home" && <HomeTab setTab={setTab} showToast={showToast} onBook={() => setBookingOpen(true)} onTrack={(l) => setTrackingLoad(l)} />}
        {tab === "shipments" && <ShipmentsTab showToast={showToast} onTrack={(l) => setTrackingLoad(l)} />}
        {tab === "invoices" && <InvoicesTab showToast={showToast} />}
        {tab === "profile" && <ProfileTab showToast={showToast} onLogout={onLogout} />}
      </div>
      <BottomNav tab={tab} setTab={setTab} onCreate={() => setBookingOpen(true)} />
      <AnimatePresence>
        {bookingOpen && <BookingSheet onClose={() => setBookingOpen(false)} showToast={showToast} onDone={() => { setBookingOpen(false); setTab("shipments"); showToast("Shipment booked · driver bidding open"); }} />}
      </AnimatePresence>
      <AnimatePresence>
        {trackingLoad && <TrackingSheet load={trackingLoad} onClose={() => setTrackingLoad(null)} showToast={showToast} />}
      </AnimatePresence>
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}
            style={{ position: "absolute", top: 64, left: "50%", transform: "translateX(-50%)", background: "rgba(20,20,22,0.96)", backdropFilter: "blur(20px)", border: `0.5px solid ${ORANGE}50`, color: "white", padding: "8px 14px", borderRadius: 999, fontSize: 12, fontFamily: apple, fontWeight: 600, zIndex: 100, boxShadow: "0 10px 30px rgba(0,0,0,0.5)", maxWidth: "85%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            <span style={{ color: ORANGE, marginRight: 6 }}>●</span>{toast}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ---------- BOTTOM NAV ---------- */
function BottomNav({ tab, setTab, onCreate }) {
  const tabs = [
    { id: "home", label: "Home", icon: <NavIcon path="M3 11l9-7 9 7v9a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2z" /> },
    { id: "shipments", label: "Shipments", icon: <NavIcon path="M3 7h13l3 4v6H3z M5 19a2 2 0 100-4 2 2 0 000 4z M17 19a2 2 0 100-4 2 2 0 000 4z" /> },
    { id: "create", label: "Create", icon: null },
    { id: "invoices", label: "Invoices", icon: <NavIcon path="M6 3h9l5 5v13a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z M14 3v6h6 M8 13h8 M8 17h8 M8 9h3" /> },
    { id: "profile", label: "Profile", icon: <NavIcon path="M12 12a4 4 0 100-8 4 4 0 000 8zm-7 9a7 7 0 0114 0z" /> },
  ];
  return (
    <div style={{ borderTop: "0.5px solid rgba(255,255,255,0.08)", background: "rgba(10,10,10,0.95)", backdropFilter: "blur(20px)", padding: "8px 0 22px", display: "flex", alignItems: "center", position: "relative" }}>
      {tabs.map((t) => {
        if (t.id === "create") {
          return (
            <div key={t.id} style={{ flex: 1, display: "flex", justifyContent: "center" }}>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onCreate}
                data-testid="nav-ship-create"
                style={{ width: 52, height: 52, borderRadius: "50%", background: ORANGE, border: "none", color: "white", cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", marginTop: -22, boxShadow: `0 8px 24px ${ORANGE}66, 0 0 0 4px #0a0a0a` }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
              </motion.button>
            </div>
          );
        }
        const active = tab === t.id;
        return (
          <motion.button
            key={t.id}
            whileTap={{ scale: 0.92 }}
            onClick={() => setTab(t.id)}
            data-testid={`nav-ship-${t.id}`}
            style={{ flex: 1, background: "none", border: "none", color: active ? ORANGE : "rgba(255,255,255,0.45)", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", gap: 3, padding: 4 }}
          >
            <div style={{ width: 22, height: 22 }}>{t.icon}</div>
            <div style={{ fontSize: 10, fontWeight: 600 }}>{t.label}</div>
          </motion.button>
        );
      })}
    </div>
  );
}
function NavIcon({ path }) {
  return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={path} /></svg>;
}

/* ---------- SHARED HEADER ---------- */
function ShipHeader({ showToast, onAvatar }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "2px 0 10px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontSize: 26, fontWeight: 900, color: ORANGE, letterSpacing: -0.5, fontStyle: "italic" }}>RY</span>
        <span style={{ fontSize: 17, fontWeight: 700, color: "white", letterSpacing: 3 }}>SHIP</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={() => showToast("3 new notifications")} data-testid="ship-bell" style={{ background: "none", border: "none", color: "white", cursor: "pointer", position: "relative", padding: 4 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
          <span style={{ position: "absolute", top: 2, right: 2, width: 7, height: 7, borderRadius: "50%", background: ORANGE, border: "1.5px solid #0a0a0a" }} />
        </button>
        <button onClick={onAvatar} data-testid="ship-avatar" style={{ width: 36, height: 36, borderRadius: "50%", border: `2px solid ${ORANGE}`, padding: 0, background: "none", cursor: "pointer", overflow: "hidden" }}>
          <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&q=80" alt="Dwayne" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" }} />
        </button>
      </div>
    </div>
  );
}

/* ---------- HOME TAB ---------- */
function HomeTab({ setTab, showToast, onBook, onTrack }) {
  const activeShipment = { id: "RY-78432", from: "Toronto, ON", to: "Chicago, IL", status: "In Transit", eta: "May 19, 2025", etaTime: "2:00 PM CDT", trailer: "Dry Van", length: "53 ft", weight: "42,000 lbs" };
  return (
    <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
      <ShipHeader showToast={showToast} onAvatar={() => setTab("profile")} />

      {/* Greeting */}
      <div style={{ padding: "8px 0 16px" }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Good morning, Dwayne</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>Track and manage your freight.</div>
      </div>

      {/* Book a Shipment hero card */}
      <button onClick={onBook} data-testid="book-shipment-hero" style={{ width: "100%", background: "#0a0a0a", border: `1px solid ${ORANGE}`, borderRadius: 18, padding: 14, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", fontFamily: apple, color: "white", textAlign: "left", boxShadow: `0 0 24px ${ORANGE}22, inset 0 0 30px ${ORANGE}11` }}>
        <div style={{ width: 56, height: 56, borderRadius: 14, background: `radial-gradient(circle at 50% 50%, ${ORANGE}33, ${ORANGE}08)`, border: `1px solid ${ORANGE}66`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
            <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
            <line x1="12" y1="22.08" x2="12" y2="12" />
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "white", letterSpacing: -0.3 }}>Book a Shipment</div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginTop: 3 }}>Get a quote and book in minutes.</div>
        </div>
        <div style={{ background: ORANGE, color: "white", padding: "9px 14px", borderRadius: 10, fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>
          Get Started <span>›</span>
        </div>
      </button>

      {/* 4 stat tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginTop: 12 }}>
        <StatTile icon={<TruckIcon />} value="12" label="Active Shipments" action="View all" onClick={() => setTab("shipments")} testid="stat-active" />
        <StatTile icon={<QuoteIcon />} value="4" label="Pending Quotes" action="View all" onClick={() => { setTab("shipments"); showToast("Showing pending quotes"); }} testid="stat-quotes" />
        <StatTile icon={<CheckIcon />} value="28" label="Delivered" action="This month" actionColor={GREEN} onClick={() => { setTab("shipments"); showToast("Showing delivered"); }} testid="stat-delivered" />
        <StatTile icon={<DollarIcon />} value="$18,540" label="Monthly Spend" action="View details" onClick={() => setTab("invoices")} testid="stat-spend" />
      </div>

      {/* Active Shipment card */}
      <div style={{ marginTop: 12, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 14, position: "relative", overflow: "hidden" }}>
        {/* truck image */}
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 200,
            height: "100%",
            background: "linear-gradient(90deg, rgba(15,15,15,0.95) 0%, rgba(15,15,15,0.4) 35%, transparent 100%), url('https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&q=70') center/cover",
            opacity: 0.85,
          }}
        />
        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: ORANGE, fontWeight: 700, letterSpacing: 1.5 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: ORANGE }} />
            ACTIVE SHIPMENT
          </div>
          <div style={{ fontSize: 19, fontWeight: 800, marginTop: 8, color: "white", letterSpacing: -0.4 }}>
            {activeShipment.from} <span style={{ color: "rgba(255,255,255,0.5)", margin: "0 6px" }}>→</span> {activeShipment.to}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 12, maxWidth: 280 }}>
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>STATUS</div>
              <div style={{ fontSize: 12, color: GREEN, marginTop: 3, fontWeight: 600 }}>{activeShipment.status}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>ETA</div>
              <div style={{ fontSize: 11, color: "white", marginTop: 3 }}>{activeShipment.eta}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>{activeShipment.etaTime}</div>
            </div>
            <div>
              <div style={{ fontSize: 9, color: "rgba(255,255,255,0.5)", letterSpacing: 1, fontWeight: 600 }}>SHIPMENT ID</div>
              <div style={{ fontSize: 11, color: "white", marginTop: 3, fontFamily: "monospace" }}>{activeShipment.id}</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 11, color: "rgba(255,255,255,0.75)" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="2" /><circle cx="16.5" cy="17.5" r="2" /></svg>
            {activeShipment.trailer} <span style={{ opacity: 0.4 }}>•</span> {activeShipment.length} <span style={{ opacity: 0.4 }}>•</span> {activeShipment.weight}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14, position: "relative", zIndex: 2 }}>
            <button onClick={() => onTrack(activeShipment)} data-testid="active-track" style={{ flex: 1, background: ORANGE, color: "white", border: "none", padding: "11px 12px", borderRadius: 11, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
              Track Shipment
            </button>
            <button onClick={() => showToast("Connecting to support…")} data-testid="active-support" style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.15)", padding: "11px 12px", borderRadius: 11, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 14v-4a9 9 0 0118 0v4M3 14a2 2 0 002 2h1v-6H5a2 2 0 00-2 2zM21 14a2 2 0 01-2 2h-1v-6h1a2 2 0 012 2z" /></svg>
              Contact Support
            </button>
          </div>
        </div>
      </div>

      {/* Recent Shipments */}
      <div style={{ marginTop: 14, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "white" }}>Recent Shipments</div>
          <button onClick={() => setTab("shipments")} data-testid="recent-viewall" style={{ background: "none", border: "none", color: ORANGE, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", gap: 3 }}>View all ›</button>
        </div>
        {[
          { id: "RY-78201", from: "Vancouver, BC", to: "Calgary, AB", trailer: "Flatbed", length: "48 ft", weight: "36,000 lbs", status: "Delivered", color: GREEN, price: "$1,850", icon: "box" },
          { id: "RY-78088", from: "Montreal, QC", to: "Toronto, ON", trailer: "Reefer", length: "53 ft", weight: "40,000 lbs", status: "In Transit", color: BLUE, price: "$2,420", icon: "container" },
          { id: "RY-77991", from: "Dallas, TX", to: "Houston, TX", trailer: "Dry Van", length: "53 ft", weight: "41,000 lbs", status: "Pending Pickup", color: ORANGE, price: "$1,320", icon: "doc" },
        ].map((s, i) => (
          <button key={s.id} onClick={() => onTrack(s)} data-testid={`recent-${s.id}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", width: "100%", background: "none", border: "none", borderTop: i ? "0.5px solid rgba(255,255,255,0.06)" : "none", color: "white", cursor: "pointer", fontFamily: apple, textAlign: "left" }}>
            <ShipTypeIcon kind={s.icon} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "white" }}>{s.from} <span style={{ color: "rgba(255,255,255,0.4)" }}>→</span> {s.to}</div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>{s.trailer} <span style={{ opacity: 0.4 }}>•</span> {s.length} <span style={{ opacity: 0.4 }}>•</span> {s.weight}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{ display: "inline-block", background: `${s.color}1f`, border: `0.5px solid ${s.color}55`, color: s.color, padding: "3px 8px", borderRadius: 999, fontSize: 9.5, fontWeight: 700 }}>{s.status}</span>
              <div style={{ fontSize: 13, fontWeight: 800, color: "white", marginTop: 4 }}>{s.price}</div>
            </div>
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 16 }}>›</span>
          </button>
        ))}
      </div>

      {/* Quick Actions */}
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "white", marginBottom: 10 }}>Quick Actions</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6 }}>
          <QuickAction icon={<QuoteIcon />} label="Request Quote" onClick={onBook} testid="qa-quote" />
          <QuickAction icon={<BoxIcon />} label="Book Shipment" onClick={onBook} testid="qa-book" />
          <QuickAction icon={<InvoiceIcon />} label="Invoices" onClick={() => setTab("invoices")} testid="qa-invoices" />
          <QuickAction icon={<HeadsetIcon />} label="Support" onClick={() => showToast("Support · 1-800-RY-SHIP")} testid="qa-support" />
        </div>
      </div>

      {/* Insights banner */}
      <div style={{ marginTop: 14, background: "rgba(255,255,255,0.03)", border: `1px solid ${ORANGE}44`, borderRadius: 16, padding: 14, display: "flex", alignItems: "center", gap: 12, position: "relative", overflow: "hidden" }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: `radial-gradient(circle at 50% 50%, ${ORANGE}33, ${ORANGE}08)`, border: `1px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "white" }}>Keep your shipments moving efficiently</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 3, lineHeight: 1.4 }}>Shipments in May are trending 12% under budget compared to April.</div>
        </div>
        <button onClick={() => showToast("Insights dashboard opened")} data-testid="insights-btn" style={{ background: "transparent", color: ORANGE, border: `1px solid ${ORANGE}`, padding: "8px 12px", borderRadius: 10, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
          View Insights <span>›</span>
        </button>
      </div>
    </div>
  );
}

/* ---------- Icons ---------- */
function TruckIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="2" /><circle cx="16.5" cy="17.5" r="2" /></svg>; }
function QuoteIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="14" y2="13" /><line x1="8" y1="17" x2="12" y2="17" /></svg>; }
function CheckIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="8 12 11 15 16 9" /></svg>; }
function DollarIcon() { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5A2.5 2.5 0 0012 8h-1a2 2 0 000 4h2a2 2 0 010 4h-1a2.5 2.5 0 01-2.5-1.5 M12 6v2M12 16v2" /></svg>; }
function BoxIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>; }
function InvoiceIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" /><line x1="8" y1="17" x2="16" y2="17" /><line x1="8" y1="9" x2="11" y2="9" /></svg>; }
function HeadsetIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 14v-4a9 9 0 0118 0v4M3 14a2 2 0 002 2h1v-6H5a2 2 0 00-2 2zM21 14a2 2 0 01-2 2h-1v-6h1a2 2 0 012 2z" /></svg>; }
function ContainerIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="12" rx="1" /><line x1="8" y1="6" x2="8" y2="18" /><line x1="13" y1="6" x2="13" y2="18" /><line x1="18" y1="6" x2="18" y2="18" /></svg>; }
function DocIcon() { return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>; }

function ShipTypeIcon({ kind }) {
  return (
    <div style={{ width: 36, height: 36, borderRadius: 10, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {kind === "box" && <BoxIcon />}
      {kind === "container" && <ContainerIcon />}
      {kind === "doc" && <DocIcon />}
    </div>
  );
}

function StatTile({ icon, value, label, action, actionColor, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "10px 8px", textAlign: "left", color: "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div style={{ fontSize: 15, fontWeight: 800, letterSpacing: -0.3, color: "white", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 8.5, color: "rgba(255,255,255,0.55)", lineHeight: 1.2 }}>{label}</div>
      <div style={{ fontSize: 9, color: actionColor || ORANGE, fontWeight: 600, marginTop: 2, display: "flex", alignItems: "center", gap: 2 }}>{action} {!actionColor && <span>›</span>}</div>
    </button>
  );
}

function QuickAction({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "14px 6px", textAlign: "center", color: "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}33`, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div style={{ fontSize: 10.5, fontWeight: 600, color: "white", lineHeight: 1.2 }}>{label}</div>
    </button>
  );
}

/* ============================================================ */
/*  SHIPMENTS TAB                                               */
/* ============================================================ */
const allShipments = [
  { id: "RY-78432", from: "Toronto, ON", to: "Chicago, IL", trailer: "Dry Van", length: "53 ft", weight: "42,000 lbs", status: "In Transit", price: "$2,420", icon: "truck", theme: GREEN,
    pickupDate: "May 19, 2025", eta: "May 20, 2025", etaTime: "2:00 PM CDT", progress: 0.72, action: "Track" },
  { id: "RY-78428", from: "Vancouver, BC", to: "Calgary, AB", trailer: "Flatbed", length: "48 ft", weight: "36,000 lbs", status: "Delivered", price: "$1,850", icon: "container", theme: GREEN,
    pickupDate: "May 14, 2025", deliveredDate: "May 16, 2025", deliveredTime: "11:32 AM MST", action: "View" },
  { id: "RY-78421", from: "Montreal, QC", to: "Toronto, ON", trailer: "Reefer", length: "53 ft", weight: "40,000 lbs", status: "In Transit", price: "$2,420", icon: "box", theme: BLUE,
    pickupDate: "May 17, 2025", eta: "May 19, 2025", etaTime: "9:00 AM EDT", progress: 0.65, action: "Track" },
  { id: "RY-78419", from: "Dallas, TX", to: "Houston, TX", trailer: "Dry Van", length: "53 ft", weight: "41,000 lbs", status: "Pending Pickup", price: "$1,320", icon: "doc", theme: ORANGE,
    pickupDate: "May 20, 2025", pickupTime: "8:00 AM CDT", eta: "May 21, 2025", etaTime: "6:00 PM CDT", action: "Details" },
  { id: "RY-78415", from: "Atlanta, GA", to: "Charlotte, NC", trailer: "Dry Van", length: "48 ft", weight: "40,000 lbs", status: "Quote Requested", price: null, icon: "quote", theme: BLUE,
    requestedDate: "May 18, 2025", action: "View Quote" },
  { id: "RY-78410", from: "Los Angeles, CA", to: "Phoenix, AZ", trailer: "Flatbed", length: "48 ft", weight: "35,000 lbs", status: "Cancelled", price: null, icon: "container", theme: "rgba(255,255,255,0.5)",
    action: "View" },
  { id: "RY-78201", from: "Hamilton, ON", to: "Buffalo, NY", trailer: "Reefer", length: "53 ft", weight: "38,500 lbs", status: "Delivered", price: "$2,080", icon: "box", theme: GREEN,
    pickupDate: "May 12, 2025", deliveredDate: "May 13, 2025", deliveredTime: "4:18 PM EDT", action: "View" },
];

const STATUS_COLOR = {
  "In Transit": GREEN,
  "Pending Pickup": ORANGE,
  "Delivered": GREEN,
  "Quote Requested": BLUE,
  "Cancelled": "rgba(255,255,255,0.5)",
};

function ShipmentsTab({ showToast, onTrack }) {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const counts = {
    All: allShipments.length,
    "In Transit": allShipments.filter(s => s.status === "In Transit").length,
    Pending: allShipments.filter(s => s.status === "Pending Pickup").length,
    Delivered: allShipments.filter(s => s.status === "Delivered").length,
    Quotes: allShipments.filter(s => s.status === "Quote Requested").length,
  };
  const filterDefs = [
    { id: "All", label: "All", count: counts.All, countColor: ORANGE },
    { id: "In Transit", label: "In Transit", count: counts["In Transit"], countColor: GREEN },
    { id: "Pending", label: "Pending", count: counts.Pending, countColor: ORANGE },
    { id: "Delivered", label: "Delivered", count: counts.Delivered, countColor: GREEN },
    { id: "Quotes", label: "Quotes", count: counts.Quotes, countColor: BLUE },
  ];

  const filterMatch = (s) => {
    if (filter === "All") return true;
    if (filter === "Pending") return s.status === "Pending Pickup";
    if (filter === "Quotes") return s.status === "Quote Requested";
    return s.status === filter;
  };
  const filtered = allShipments
    .filter(filterMatch)
    .filter(s => !query || `${s.from} ${s.to} ${s.id}`.toLowerCase().includes(query.toLowerCase()));

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
      <ShipHeader showToast={showToast} onAvatar={() => showToast("Profile opened")} />

      {/* Title */}
      <div style={{ padding: "4px 0 12px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Shipments</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>Track, manage and view all your shipments.</div>
      </div>

      {/* Search + filter icon */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "9px 12px", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.5)"><path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" /></svg>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search shipment ID, route, or customer ref"
            data-testid="ship-search"
            style={{ background: "transparent", border: "none", outline: "none", color: "white", fontSize: 12, flex: 1, fontFamily: apple }} />
        </div>
        <button onClick={() => showToast("Advanced filters")} data-testid="ship-filter-btn"
          style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "0 10px", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="9" y1="18" x2="15" y2="18" /><circle cx="16" cy="6" r="1.5" fill="currentColor" stroke="none" /><circle cx="8" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="12" cy="18" r="1.5" fill="currentColor" stroke="none" /></svg>
        </button>
      </div>

      {/* Filter chips with count badges */}
      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }} className="scrollbar-hide">
        {filterDefs.map((f) => {
          const active = filter === f.id;
          return (
            <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`ship-filter-${f.id.toLowerCase().replace(/\s/g, "")}`}
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${active ? ORANGE : "rgba(255,255,255,0.12)"}`, color: "white", padding: "9px 14px", borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: apple, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, display: "flex", alignItems: "center", gap: 8, boxShadow: active ? `0 0 16px ${ORANGE}33` : "none" }}>
              {f.label}
              <span style={{ background: `${f.countColor}22`, border: `0.5px solid ${f.countColor}66`, color: f.countColor, fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{f.count}</span>
            </button>
          );
        })}
      </div>

      {/* Count + sort row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}><span style={{ color: "white", fontWeight: 700 }}>{filtered.length}</span> shipments</div>
        <button onClick={() => showToast("Sort options")} data-testid="ship-sort"
          style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "6px 10px", borderRadius: 10, fontSize: 11, fontWeight: 600, fontFamily: apple, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h13M3 12h9M3 18h5M17 6v12M14 15l3 3 3-3" /></svg>
          Sorted by: <span style={{ color: ORANGE, fontWeight: 700 }}>Latest</span>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 8 }}>▾</span>
        </button>
      </div>

      {/* Shipment cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map((s) => (
          <ShipmentCard key={s.id} s={s} onClick={() => onTrack(s)} onAction={() => onTrack(s)} />
        ))}
      </div>
    </div>
  );
}

/* ---------- SHIPMENT CARD ---------- */
function ShipmentCard({ s, onClick, onAction }) {
  const statusColor = STATUS_COLOR[s.status] || "rgba(255,255,255,0.5)";
  const theme = s.theme || statusColor;
  const showProgress = s.status === "In Transit";
  const showPickupOnly = s.status === "Pending Pickup";
  const showDelivered = s.status === "Delivered";
  const showRequested = s.status === "Quote Requested";
  const showCancelled = s.status === "Cancelled";
  const hasFooter = showProgress || showPickupOnly || showDelivered || showRequested || showCancelled;

  return (
    <div onClick={onClick} role="button" tabIndex={0} data-testid={`shipment-${s.id}`} style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 16, color: "white", cursor: "pointer", fontFamily: apple }}>
      {/* Top: icon + content + status pill */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <CardIcon kind={s.icon} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: "white", letterSpacing: -0.2, lineHeight: 1.25 }}>
                {s.from} <span style={{ color: "rgba(255,255,255,0.45)", margin: "0 2px" }}>→</span> {s.to}
              </div>
              <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>ID: <span style={{ color: "rgba(255,255,255,0.7)" }}>{s.id}</span></div>
            </div>
            <span style={{ background: "transparent", border: `1px solid ${statusColor}`, color: statusColor, padding: "3px 9px", borderRadius: 999, fontSize: 9.5, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>{s.status}</span>
          </div>
          {/* equipment row */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "rgba(255,255,255,0.65)", marginTop: 8 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="1.6" /><circle cx="16.5" cy="17.5" r="1.6" /></svg>
            {s.trailer} <span style={{ opacity: 0.4 }}>•</span> {s.length} <span style={{ opacity: 0.4 }}>•</span> {s.weight}
          </div>
        </div>
      </div>

      {/* Footer: status content + price/action */}
      {hasFooter && (
        <div style={{ borderTop: "0.5px solid rgba(255,255,255,0.06)", marginTop: 14, paddingTop: 14 }}>
          {showProgress && (
            <>
              {/* progress row */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <DateBlock icon={<TickIcon color={GREEN} />} kicker="PICKUP" value={s.pickupDate} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <ProgressBar progress={s.progress || 0.5} color={theme} />
                </div>
                <DateBlock kicker="ETA" value={s.eta} sub={s.etaTime} align="right" />
              </div>
              <FooterAction s={s} onAction={onAction} />
            </>
          )}
          {showDelivered && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
                <DateBlock icon={<TickIcon color={GREEN} />} kicker="PICKUP" value={s.pickupDate} />
                <DateBlock kicker="DELIVERED" value={s.deliveredDate} sub={s.deliveredTime} align="right" />
              </div>
              <FooterAction s={s} onAction={onAction} />
            </>
          )}
          {showPickupOnly && (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
                <DateBlock icon={<EmptyCircle />} kicker="PICKUP" value={s.pickupDate} sub={s.pickupTime} />
                <DateBlock kicker="ETA" value={s.eta} sub={s.etaTime} align="right" />
              </div>
              <FooterAction s={s} onAction={onAction} />
            </>
          )}
          {showRequested && (
            <>
              <div style={{ marginBottom: 14 }}>
                <DateBlock kicker="REQUESTED" value={s.requestedDate} />
              </div>
              <FooterAction s={s} onAction={onAction} />
            </>
          )}
          {showCancelled && <FooterAction s={s} onAction={onAction} />}
        </div>
      )}
    </div>
  );
}

function FooterAction({ s, onAction }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        {s.price && <span style={{ fontSize: 18, fontWeight: 800, color: "white", letterSpacing: -0.4 }}>{s.price}</span>}
        {!s.price && <span style={{ fontSize: 14, color: "rgba(255,255,255,0.4)" }}>—</span>}
      </div>
      <ActionBtn label={s.action} onClick={(e) => { e.stopPropagation(); onAction(); }} testid={`action-${s.id}`} />
    </div>
  );
}

function CardIcon({ kind }) {
  const icon = {
    truck: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="2" /><circle cx="16.5" cy="17.5" r="2" /></svg>,
    container: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="18" height="12" rx="1" /><line x1="8" y1="6" x2="8" y2="18" /><line x1="13" y1="6" x2="13" y2="18" /><line x1="18" y1="6" x2="18" y2="18" /></svg>,
    box: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" /><polyline points="3.27 6.96 12 12.01 20.73 6.96" /><line x1="12" y1="22.08" x2="12" y2="12" /></svg>,
    doc: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><circle cx="12" cy="15" r="2" /><path d="M11 13v-1m1 0v1m1.5 0v.5M9 17h6" /></svg>,
    quote: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="14" y2="13" /><line x1="8" y1="17" x2="12" y2="17" /></svg>,
  };
  return (
    <div style={{ width: 44, height: 44, borderRadius: "50%", background: `${ORANGE}14`, border: `1px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon[kind] || icon.truck}
    </div>
  );
}

function DateBlock({ icon, kicker, value, sub, align = "left" }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {icon && align === "left" && <div style={{ flexShrink: 0 }}>{icon}</div>}
      <div style={{ minWidth: 0, textAlign: align }}>
        <div style={{ fontSize: 9, color: "rgba(255,255,255,0.55)", letterSpacing: 1, fontWeight: 700 }}>{kicker}</div>
        <div style={{ fontSize: 11.5, color: "white", fontWeight: 600, marginTop: 2, whiteSpace: "nowrap" }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 1, whiteSpace: "nowrap" }}>{sub}</div>}
      </div>
    </div>
  );
}

function TickIcon({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <polyline points="8 12 11 15 16 9" />
    </svg>
  );
}

function EmptyCircle() {
  return <div style={{ width: 14, height: 14, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.35)" }} />;
}

function ProgressBar({ progress, color }) {
  const truckPos = Math.min(0.92, Math.max(0.08, progress));
  return (
    <div style={{ position: "relative", height: 18 }}>
      {/* base rail */}
      <div style={{ position: "absolute", left: 4, right: 4, top: 8, height: 2, background: "rgba(255,255,255,0.12)", borderRadius: 1 }} />
      {/* filled rail */}
      <div style={{ position: "absolute", left: 4, top: 8, height: 2, width: `calc(${truckPos * 100}% - 4px)`, background: color, borderRadius: 1, boxShadow: `0 0 8px ${color}` }} />
      {/* start dot */}
      <div style={{ position: "absolute", left: 0, top: 4, width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 6px ${color}` }} />
      {/* end dot */}
      <div style={{ position: "absolute", right: 0, top: 4, width: 8, height: 8, borderRadius: "50%", background: "rgba(255,255,255,0.2)" }} />
      {/* truck pill */}
      <div style={{ position: "absolute", left: `${truckPos * 100}%`, top: -2, transform: "translateX(-50%)" }}>
        <div style={{ background: color, color: "white", borderRadius: 6, padding: "2px 4px", display: "flex", alignItems: "center", boxShadow: `0 0 10px ${color}` }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="1.4" /><circle cx="16.5" cy="17.5" r="1.4" /></svg>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ background: "transparent", border: `1px solid ${ORANGE}`, color: ORANGE, padding: "9px 22px", borderRadius: 10, fontSize: 12, fontWeight: 700, fontFamily: apple, cursor: "pointer", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );
}

/* ============================================================ */
/*  INVOICES TAB                                                */
/* ============================================================ */
const allInvoices = [
  { id: "INV-78432", from: "Toronto, ON", to: "Chicago, IL", trailer: "Dry Van", length: "53 ft", weight: "42,000 lbs", amt: "$2,420.00", billed: "May 19, 2025", due: "Jun 2, 2025", status: "Paid" },
  { id: "INV-78428", from: "Vancouver, BC", to: "Calgary, AB", trailer: "Flatbed", length: "48 ft", weight: "36,000 lbs", amt: "$1,850.00", billed: "May 14, 2025", due: "May 28, 2025", status: "Pending" },
  { id: "INV-78421", from: "Montreal, QC", to: "Toronto, ON", trailer: "Reefer", length: "53 ft", weight: "40,000 lbs", amt: "$3,120.00", billed: "May 17, 2025", due: "May 20, 2025", status: "Overdue" },
  { id: "INV-78419", from: "Dallas, TX", to: "Houston, TX", trailer: "Dry Van", length: "53 ft", weight: "41,000 lbs", amt: "$1,320.00", billed: "May 20, 2025", due: "—", status: "Draft" },
  { id: "INV-78410", from: "Los Angeles, CA", to: "Phoenix, AZ", trailer: "Flatbed", length: "48 ft", weight: "35,000 lbs", amt: "$2,050.00", billed: "May 18, 2025", due: "May 21, 2025", status: "Paid" },
  { id: "INV-78401", from: "Hamilton, ON", to: "Buffalo, NY", trailer: "Reefer", length: "53 ft", weight: "38,500 lbs", amt: "$2,080.00", billed: "May 12, 2025", due: "May 26, 2025", status: "Pending" },
  { id: "INV-78388", from: "Atlanta, GA", to: "Charlotte, NC", trailer: "Dry Van", length: "48 ft", weight: "40,000 lbs", amt: "$1,420.00", billed: "May 10, 2025", due: "May 12, 2025", status: "Overdue" },
];

function InvoicesTab({ showToast }) {
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");

  const counts = {
    All: allInvoices.length,
    Paid: allInvoices.filter(i => i.status === "Paid").length,
    Pending: allInvoices.filter(i => i.status === "Pending").length,
    Overdue: allInvoices.filter(i => i.status === "Overdue").length,
    Draft: allInvoices.filter(i => i.status === "Draft").length,
  };

  const filtered = allInvoices
    .filter(i => filter === "All" ? true : i.status === filter)
    .filter(i => !query || `${i.from} ${i.to} ${i.id}`.toLowerCase().includes(query.toLowerCase()));

  const sum = (status) => allInvoices.filter(i => i.status === status).reduce((acc, i) => acc + parseFloat(i.amt.replace(/[$,]/g, "")), 0);
  const fmt = (n) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const openBalance = sum("Pending") + sum("Overdue");

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
      <ShipHeader showToast={showToast} onAvatar={() => showToast("Profile opened")} />

      {/* Title */}
      <div style={{ padding: "4px 0 14px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Invoices</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>View, download and manage your billing.</div>
      </div>

      {/* Search + filter icon */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <div style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "9px 12px", display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="rgba(255,255,255,0.5)"><path d="M10 4a6 6 0 014.8 9.6l5.3 5.3-1.4 1.4-5.3-5.3A6 6 0 1110 4zm0 2a4 4 0 100 8 4 4 0 000-8z" /></svg>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search invoice ID, shipment, or customer ref"
            data-testid="inv-search"
            style={{ background: "transparent", border: "none", outline: "none", color: "white", fontSize: 12, flex: 1, fontFamily: apple }} />
        </div>
        <button onClick={() => showToast("Advanced filters")} data-testid="inv-filter-btn"
          style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "0 10px", borderRadius: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><line x1="4" y1="6" x2="20" y2="6" /><line x1="6" y1="12" x2="18" y2="12" /><line x1="9" y1="18" x2="15" y2="18" /><circle cx="16" cy="6" r="1.5" fill="currentColor" stroke="none" /><circle cx="8" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="12" cy="18" r="1.5" fill="currentColor" stroke="none" /></svg>
        </button>
      </div>

      {/* Filter chips with counts */}
      <div style={{ display: "flex", gap: 8, overflowX: "auto", marginBottom: 12, paddingBottom: 2 }} className="scrollbar-hide">
        {["All", "Paid", "Pending", "Overdue", "Draft"].map((f) => {
          const active = filter === f;
          return (
            <button key={f} onClick={() => setFilter(f)} data-testid={`inv-filter-${f.toLowerCase()}`}
              style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${active ? ORANGE : "rgba(255,255,255,0.12)"}`, color: "white", padding: "9px 14px", borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: apple, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0, display: "flex", alignItems: "center", gap: 8, boxShadow: active ? `0 0 16px ${ORANGE}33` : "none" }}>
              {f}
              <span style={{ background: active ? `${ORANGE}22` : "rgba(255,255,255,0.06)", border: `0.5px solid ${active ? ORANGE + "66" : "rgba(255,255,255,0.15)"}`, color: active ? ORANGE : "rgba(255,255,255,0.7)", fontSize: 10, fontWeight: 700, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 999, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{counts[f]}</span>
            </button>
          );
        })}
      </div>

      {/* Sort row (right-aligned pill) */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <button onClick={() => showToast("Sort options")} data-testid="inv-sort"
          style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.1)", color: "white", padding: "7px 12px", borderRadius: 10, fontSize: 11, fontWeight: 600, fontFamily: apple, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 6h13M3 12h9M3 18h5M17 6v12M14 15l3 3 3-3" /></svg>
          Sorted by: <span style={{ color: "white", fontWeight: 700 }}>Latest</span>
          <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 8 }}>▾</span>
        </button>
      </div>

      {/* 4 stat tiles - 2x2 for better legibility on narrow iPhone */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8, marginBottom: 14 }}>
        <InvStat icon={<WalletIcon />} label="Open Balance" amount={fmt(openBalance)} sub={`${counts.Pending + counts.Overdue} invoices`} />
        <InvStat icon={<DollarCircleIcon />} label="Paid This Month" amount={fmt(sum("Paid"))} sub={`${counts.Paid} invoices`} />
        <InvStat icon={<ClockArrowIcon />} label="Pending" amount={fmt(sum("Pending"))} sub={`${counts.Pending} invoices`} />
        <InvStat icon={<AlertTriIcon />} label="Overdue" amount={fmt(sum("Overdue"))} sub={`${counts.Overdue} invoices`} />
      </div>

      {/* Invoice cards */}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {filtered.map((inv) => (
          <InvoiceCard key={inv.id} inv={inv} onClick={() => showToast(`Invoice ${inv.id} opened`)} onAction={(action) => showToast(`${action} · ${inv.id}`)} />
        ))}
      </div>
    </div>
  );
}

function InvStat({ icon, label, amount, sub }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 14, minWidth: 0 }}>
      <div style={{ width: 30, height: 30, borderRadius: "50%", border: `1px solid ${ORANGE}66`, background: `${ORANGE}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 10, fontWeight: 500 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color: "white", marginTop: 2, letterSpacing: -0.4, whiteSpace: "nowrap" }}>{amount}</div>
      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.45)", marginTop: 3 }}>{sub}</div>
    </div>
  );
}

const STATUS_META = {
  Paid:    { color: ORANGE, action: "Download", iconKind: "doc-dollar" },
  Pending: { color: ORANGE, action: "View",     iconKind: "clock" },
  Overdue: { color: ORANGE, action: "Pay Now",  iconKind: "alert" },
  Draft:   { color: "rgba(255,255,255,0.6)", action: "View", iconKind: "doc-dollar" },
};

function InvoiceCard({ inv, onClick, onAction }) {
  const meta = STATUS_META[inv.status] || STATUS_META.Pending;
  return (
    <div onClick={onClick} role="button" tabIndex={0} data-testid={`inv-${inv.id}`}
      style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 18, padding: 16, color: "white", cursor: "pointer", fontFamily: apple }}>
      {/* Top: icon + (id + lane + equipment) + status pill */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: `${ORANGE}14`, border: `1px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <InvCardIcon kind={meta.iconKind} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: "white", letterSpacing: -0.2 }}>{inv.id}</div>
            <span style={{ background: "transparent", border: `1px solid ${meta.color}`, color: meta.color, padding: "3px 10px", borderRadius: 999, fontSize: 10, fontWeight: 700, whiteSpace: "nowrap", flexShrink: 0 }}>{inv.status}</span>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "white", marginTop: 4, lineHeight: 1.25 }}>
            {inv.from} <span style={{ color: "rgba(255,255,255,0.45)", margin: "0 2px" }}>→</span> {inv.to}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10.5, color: "rgba(255,255,255,0.6)", marginTop: 6 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" style={{ flexShrink: 0 }}><path d="M3 7h13l3 4v6H3z" /><circle cx="6.5" cy="17.5" r="1.6" /><circle cx="16.5" cy="17.5" r="1.6" /></svg>
            <span>{inv.trailer} <span style={{ opacity: 0.4 }}>•</span> {inv.length} <span style={{ opacity: 0.4 }}>•</span> {inv.weight}</span>
          </div>
        </div>
      </div>

      {/* Divider + dates */}
      <div style={{ borderTop: "0.5px solid rgba(255,255,255,0.06)", marginTop: 14, paddingTop: 12, display: "flex", gap: 18, fontSize: 10.5, color: "rgba(255,255,255,0.55)" }}>
        <span>Billed: <span style={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>{inv.billed}</span></span>
        <span>Due: <span style={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>{inv.due}</span></span>
      </div>

      {/* Bottom: amount + action */}
      <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: "white", letterSpacing: -0.5 }}>{inv.amt}</div>
        <button onClick={(e) => { e.stopPropagation(); onAction(meta.action); }} data-testid={`inv-action-${inv.id}`}
          style={{ background: "transparent", border: `1px solid ${ORANGE}`, color: ORANGE, padding: "9px 22px", borderRadius: 10, fontSize: 12, fontWeight: 700, fontFamily: apple, cursor: "pointer", whiteSpace: "nowrap" }}>{meta.action}</button>
      </div>
    </div>
  );
}

function InvCardIcon({ kind }) {
  if (kind === "clock") {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>;
  }
  if (kind === "alert") {
    return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.86l-8.1 14a2 2 0 001.71 3h16.2a2 2 0 001.71-3l-8.1-14a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><circle cx="12" cy="17" r="0.7" fill={ORANGE} /></svg>;
  }
  // doc-dollar default
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <circle cx="14" cy="16" r="2.6" />
      <path d="M13.2 14.8c.5-.4 1.4-.3 1.7.3M14.8 17.5c-.5.3-1.4.2-1.7-.4M14 13.7v.6M14 17.8v.7" />
    </svg>
  );
}

function WalletIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7a2 2 0 012-2h12l4 5v9a2 2 0 01-2 2H5a2 2 0 01-2-2z" /><path d="M17 5v6h4" /><circle cx="17" cy="14" r="1.2" fill={ORANGE} /></svg>;
}
function DollarCircleIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5A2.5 2.5 0 0012 8h-1a2 2 0 000 4h2a2 2 0 010 4h-1a2.5 2.5 0 01-2.5-1.5 M12 6v2M12 16v2" /></svg>;
}
function ClockArrowIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>;
}
function AlertTriIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.3 3.86l-8.1 14a2 2 0 001.71 3h16.2a2 2 0 001.71-3l-8.1-14a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><circle cx="12" cy="17" r="0.7" fill={ORANGE} /></svg>;
}

/* ============================================================ */
/*  PROFILE TAB                                                 */
/* ============================================================ */
function ProfileTab({ showToast, onLogout }) {
  const [pushOn, setPushOn] = useState(true);
  const [emailOn, setEmailOn] = useState(true);
  const [smsOn, setSmsOn] = useState(false);
  const [biometricOn, setBiometricOn] = useState(true);

  // Tier progress
  const tierSpend = 17540;
  const nextTierAt = 20000;
  const tierPct = Math.min(1, tierSpend / nextTierAt);
  const toGo = nextTierAt - tierSpend;

  return (
    <div style={{ flex: 1, overflow: "auto", padding: "0 16px 24px" }}>
      <ShipHeader showToast={showToast} onAvatar={() => {}} />
      <div style={{ padding: "4px 0 14px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: "white", letterSpacing: -0.8 }}>Profile</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", marginTop: 3 }}>Account, payments, preferences & security.</div>
      </div>

      {/* HERO CARD */}
      <div style={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #1a0f08 0%, #0a0a0a 60%)", border: `1px solid ${ORANGE}40`, borderRadius: 22, padding: 18, marginBottom: 12 }}>
        {/* glow */}
        <div style={{ position: "absolute", top: -40, right: -40, width: 180, height: 180, borderRadius: "50%", background: `radial-gradient(circle, ${ORANGE}33, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", inset: 0, opacity: 0.15, pointerEvents: "none", backgroundImage: `linear-gradient(${ORANGE}11 1px, transparent 1px), linear-gradient(90deg, ${ORANGE}11 1px, transparent 1px)`, backgroundSize: "20px 20px" }} />

        <div style={{ position: "relative", display: "flex", gap: 14, alignItems: "center" }}>
          {/* Avatar with ring */}
          <div style={{ position: "relative" }}>
            <div style={{ width: 78, height: 78, borderRadius: "50%", padding: 3, background: `conic-gradient(from 180deg, ${ORANGE}, #fff200, ${ORANGE})`, boxShadow: `0 0 24px ${ORANGE}66` }}>
              <div style={{ width: "100%", height: "100%", borderRadius: "50%", padding: 2, background: "#0a0a0a" }}>
                <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&q=80" alt="Dwayne" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%", display: "block" }} />
              </div>
            </div>
            <div style={{ position: "absolute", bottom: -2, right: -2, width: 24, height: 24, borderRadius: "50%", background: GREEN, border: "2.5px solid #0a0a0a", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><polyline points="5 12 10 17 19 8" /></svg>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "white", letterSpacing: -0.4 }}>Dwayne Johnson</div>
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>Logistics Manager · Acme Mfg.</div>
            <div style={{ display: "flex", gap: 6, marginTop: 7, flexWrap: "wrap" }}>
              <span style={{ background: `linear-gradient(135deg, ${ORANGE}, #ffaa00)`, color: "white", padding: "3px 9px", borderRadius: 999, fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, display: "inline-flex", alignItems: "center", gap: 4 }}>★ GOLD SHIPPER</span>
              <span style={{ background: "transparent", border: `0.5px solid ${GREEN}55`, color: GREEN, padding: "3px 9px", borderRadius: 999, fontSize: 9, fontWeight: 700 }}>✓ Verified</span>
            </div>
          </div>
        </div>

        {/* Tier progress */}
        <div style={{ position: "relative", marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, marginBottom: 5 }}>
            <span style={{ color: "rgba(255,255,255,0.6)", letterSpacing: 1, fontWeight: 600 }}>NEXT TIER · PLATINUM</span>
            <span style={{ color: ORANGE, fontWeight: 700 }}>${toGo.toLocaleString()} to go</span>
          </div>
          <div style={{ height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${tierPct * 100}%`, background: `linear-gradient(90deg, ${ORANGE}, #fff200)`, borderRadius: 3, boxShadow: `0 0 10px ${ORANGE}` }} />
          </div>
        </div>

        {/* Edit Profile / Share */}
        <div style={{ position: "relative", display: "flex", gap: 8, marginTop: 14 }}>
          <button onClick={() => showToast("Edit Profile")} data-testid="profile-edit" style={{ flex: 1, background: ORANGE, border: "none", color: "white", padding: "9px 12px", borderRadius: 10, fontSize: 12, fontWeight: 700, fontFamily: apple, cursor: "pointer" }}>Edit Profile</button>
          <button onClick={() => showToast("Share QR · RY-SHP-04821")} data-testid="profile-qr" style={{ width: 38, background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="3" height="3" /><rect x="18" y="14" width="3" height="3" /><rect x="14" y="18" width="3" height="3" /></svg>
          </button>
          <button onClick={() => showToast("Notifications")} data-testid="profile-notif-shortcut" style={{ width: 38, background: "rgba(255,255,255,0.06)", border: "0.5px solid rgba(255,255,255,0.12)", color: "white", borderRadius: 10, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 16v-5a6 6 0 10-12 0v5l-2 2v1h16v-1l-2-2zM10 21a2 2 0 004 0" /></svg>
            <span style={{ position: "absolute", top: 6, right: 8, width: 6, height: 6, borderRadius: "50%", background: ORANGE }} />
          </button>
        </div>
      </div>

      {/* 3 stat tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6, marginBottom: 14 }}>
        <ProfileStat value="142" label="Shipments" sub="all time" />
        <ProfileStat value="99.2%" label="On-time" sub="last 90d" color={GREEN} />
        <ProfileStat value="$17.5k" label="Total spend" sub="this year" />
      </div>

      {/* Quick actions */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 6, marginBottom: 14 }}>
        <QuickChip icon={<WalletIcon />} label="Wallet" onClick={() => showToast("Wallet · $1,240 credit")} testid="qa-wallet" />
        <QuickChip icon={<PinIcon />} label="Addresses" onClick={() => showToast("3 saved addresses")} testid="qa-addr" />
        <QuickChip icon={<TeamIcon />} label="Team" onClick={() => showToast("Team · 5 members")} testid="qa-team" />
        <QuickChip icon={<DownloadIcon />} label="Statements" onClick={() => showToast("Statements")} testid="qa-stmt" />
      </div>

      {/* Payment Methods (visual cards) */}
      <ProfileSection title="Payment Methods" action="Add new" onAction={() => showToast("Add payment method")} testid="add-payment">
        <PaymentCard brand="VISA" last4="4148" exp="09/28" gradient="linear-gradient(135deg, #2a2a2c, #1a1a1c)" isDefault testid="card-visa" />
        <PaymentCard brand="ACH · TD" last4="8421" exp="Routing 004" gradient="linear-gradient(135deg, #0f3b2a, #06251a)" testid="card-ach" />
      </ProfileSection>

      {/* Saved Addresses */}
      <ProfileSection title="Saved Addresses" action="Manage" onAction={() => showToast("Manage addresses")} testid="manage-addresses">
        <AddressRow label="HQ — Acme Mfg." line="120 Front St W, Toronto, ON · M5J 1E3" isPrimary />
        <AddressRow label="Plant 2" line="2400 Industrial Pkwy, Hamilton, ON · L8N 3K5" />
        <AddressRow label="Distribution Center" line="980 Logistics Dr, Mississauga, ON · L5N 6P8" />
      </ProfileSection>

      {/* Team & Access */}
      <ProfileSection title="Team & Access" action="Manage team" onAction={() => showToast("Manage team")} testid="manage-team">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4 }}>
          <div style={{ display: "flex" }}>
            {[
              { c: "linear-gradient(135deg, #ff6b00, #b34800)", t: "SC" },
              { c: "linear-gradient(135deg, #3b82f6, #1d4ed8)", t: "MR" },
              { c: "linear-gradient(135deg, #10b981, #047857)", t: "JK" },
              { c: "linear-gradient(135deg, #8b5cf6, #5b21b6)", t: "+2" },
            ].map((a, i) => (
              <div key={i} style={{ width: 32, height: 32, borderRadius: "50%", background: a.c, border: "2px solid #0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "white", marginLeft: i ? -10 : 0 }}>{a.t}</div>
            ))}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "white" }}>5 team members</div>
            <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>1 Admin · 2 Managers · 2 Viewers</div>
          </div>
        </div>
      </ProfileSection>

      {/* Preferences */}
      <ProfileSection title="Shipping Preferences">
        <SettingRow label="Default Trailer" value="Dry Van 53'" onClick={() => showToast("Choose default trailer")} />
        <SettingRow label="Default Origin" value="Toronto, ON" onClick={() => showToast("Choose default origin")} />
        <SettingRow label="Currency" value="CAD · $" onClick={() => showToast("Choose currency")} />
        <SettingRow label="Auto-accept under" value="$2,800" onClick={() => showToast("Set threshold")} last />
      </ProfileSection>

      {/* Notifications */}
      <ProfileSection title="Notifications">
        <ToggleRow label="Push notifications" sub="Status updates, bids, ETA changes" on={pushOn} onChange={() => { setPushOn(!pushOn); showToast(`Push ${!pushOn ? "on" : "off"}`); }} testid="toggle-push" />
        <ToggleRow label="Email" sub="Daily digest + invoices" on={emailOn} onChange={() => { setEmailOn(!emailOn); showToast(`Email ${!emailOn ? "on" : "off"}`); }} testid="toggle-email" />
        <ToggleRow label="SMS" sub="Urgent alerts only" on={smsOn} onChange={() => { setSmsOn(!smsOn); showToast(`SMS ${!smsOn ? "on" : "off"}`); }} testid="toggle-sms" last />
      </ProfileSection>

      {/* Security */}
      <ProfileSection title="Security">
        <ToggleRow label="Face ID / Biometric" sub="Required to authorize payouts" on={biometricOn} onChange={() => { setBiometricOn(!biometricOn); showToast(`Biometric ${!biometricOn ? "on" : "off"}`); }} testid="toggle-biometric" />
        <SettingRow label="Two-factor auth" value={<span style={{ color: GREEN }}>● ON</span>} onClick={() => showToast("Manage 2FA")} />
        <SettingRow label="Active sessions" value="3 devices" onClick={() => showToast("Active sessions")} />
        <SettingRow label="Change password" value="" onClick={() => showToast("Change password")} last />
      </ProfileSection>

      {/* Company Info */}
      <ProfileSection title="Company">
        <SettingRow label="Company" value="Acme Manufacturing Inc." onClick={() => showToast("Company details")} />
        <SettingRow label="Account ID" value="RY-SHP-04821" onClick={() => showToast("Copied · RY-SHP-04821")} />
        <SettingRow label="Billing terms" value="Net 30 · ACH" onClick={() => showToast("Billing terms")} last />
      </ProfileSection>

      {/* Help & Legal */}
      <ProfileSection title="Help & Legal">
        <SettingRow icon="?" label="Help Center" onClick={() => showToast("Help Center")} />
        <SettingRow icon="✉" label="Contact Support" sub="Avg reply · 12 min" onClick={() => showToast("Support · 1-800-RY-SHIP")} />
        <SettingRow icon="§" label="Terms of Service" onClick={() => showToast("Terms opened")} />
        <SettingRow icon="🔒" label="Privacy Policy" onClick={() => showToast("Privacy opened")} last />
      </ProfileSection>

      {/* Sign out */}
      <button onClick={onLogout} data-testid="ship-signout" style={{ width: "100%", background: "rgba(239,68,68,0.08)", border: "0.5px solid rgba(239,68,68,0.35)", color: "#ef4444", padding: "14px", borderRadius: 14, fontFamily: apple, fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 14 }}>Sign Out</button>

      <div style={{ textAlign: "center", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 1 }}>
        RY SHIP <span style={{ color: ORANGE }}>·</span> v2.4.1 (build 1942)
      </div>
    </div>
  );
}

/* ---------- Profile sub-components ---------- */
function ProfileSection({ title, action, onAction, testid, children }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.07)", borderRadius: 18, padding: 14, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "white", letterSpacing: -0.1 }}>{title}</div>
        {action && (
          <button onClick={onAction} data-testid={testid} style={{ background: "none", border: "none", color: ORANGE, fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: apple, display: "flex", alignItems: "center", gap: 3 }}>
            {action} <span>›</span>
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function ProfileStat({ value, label, sub, color }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.025)", border: "0.5px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "12px 10px", textAlign: "left" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: color || "white", letterSpacing: -0.5 }}>{value}</div>
      <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.7)", marginTop: 2, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 9, color: "rgba(255,255,255,0.4)", marginTop: 1 }}>{sub}</div>
    </div>
  );
}

function QuickChip({ icon, label, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "12px 6px", color: "white", cursor: "pointer", fontFamily: apple, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
      <div style={{ width: 32, height: 32, borderRadius: 10, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}55`, display: "flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
      <div style={{ fontSize: 10, fontWeight: 600 }}>{label}</div>
    </button>
  );
}

function PaymentCard({ brand, last4, exp, gradient, isDefault, testid }) {
  return (
    <div data-testid={testid} style={{ background: gradient, borderRadius: 14, padding: 14, marginBottom: 10, position: "relative", overflow: "hidden", border: isDefault ? `1px solid ${ORANGE}66` : "0.5px solid rgba(255,255,255,0.08)", boxShadow: isDefault ? `0 0 18px ${ORANGE}22` : "none" }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.04)" }} />
      <div style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", letterSpacing: 2, fontWeight: 700 }}>{brand}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14 }}>
            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", letterSpacing: 3, fontFamily: "monospace" }}>•••• ••••</span>
            <span style={{ fontSize: 14, color: "white", letterSpacing: 2, fontWeight: 700, fontFamily: "monospace" }}>{last4}</span>
          </div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", marginTop: 6 }}>{exp}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          {/* chip */}
          <div style={{ width: 28, height: 22, borderRadius: 4, background: "linear-gradient(135deg, #c9a04c, #8b6914)", border: "0.5px solid rgba(255,255,255,0.15)" }} />
          {isDefault && <span style={{ background: `${ORANGE}26`, border: `0.5px solid ${ORANGE}`, color: ORANGE, padding: "2px 7px", borderRadius: 999, fontSize: 8.5, fontWeight: 800, letterSpacing: 0.5 }}>DEFAULT</span>}
        </div>
      </div>
    </div>
  );
}

function AddressRow({ label, line, isPrimary }) {
  return (
    <div style={{ display: "flex", gap: 10, padding: "10px 0", borderTop: "0.5px solid rgba(255,255,255,0.06)", alignItems: "flex-start" }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: `${ORANGE}1a`, border: `0.5px solid ${ORANGE}44`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: "white" }}>{label}</div>
          {isPrimary && <span style={{ background: `${ORANGE}22`, border: `0.5px solid ${ORANGE}66`, color: ORANGE, padding: "1px 6px", borderRadius: 999, fontSize: 8, fontWeight: 800 }}>PRIMARY</span>}
        </div>
        <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{line}</div>
      </div>
    </div>
  );
}

function SettingRow({ icon, label, sub, value, onClick, last }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", width: "100%", background: "none", border: "none", borderBottom: last ? "none" : "0.5px solid rgba(255,255,255,0.05)", color: "white", cursor: "pointer", fontFamily: apple, textAlign: "left" }}>
      {icon && (
        <div style={{ width: 26, height: 26, borderRadius: 8, background: `${ORANGE}14`, border: `0.5px solid ${ORANGE}33`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: ORANGE, fontSize: 12 }}>{icon}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "white" }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>{sub}</div>}
      </div>
      {value && <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.7)", fontWeight: 500, whiteSpace: "nowrap" }}>{value}</div>}
      <span style={{ color: "rgba(255,255,255,0.35)", fontSize: 14 }}>›</span>
    </button>
  );
}

function ToggleRow({ label, sub, on, onChange, testid, last }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: last ? "none" : "0.5px solid rgba(255,255,255,0.05)" }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: "white" }}>{label}</div>
        {sub && <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>{sub}</div>}
      </div>
      <button onClick={onChange} data-testid={testid} role="switch" aria-checked={on}
        style={{ width: 40, height: 24, borderRadius: 999, background: on ? ORANGE : "rgba(255,255,255,0.1)", border: "none", position: "relative", cursor: "pointer", padding: 0, transition: "background 0.2s", boxShadow: on ? `0 0 10px ${ORANGE}66` : "none" }}>
        <div style={{ width: 20, height: 20, borderRadius: "50%", background: "white", position: "absolute", top: 2, left: on ? 18 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
      </button>
    </div>
  );
}

/* Icons */
function PinIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>;
}
function TeamIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="4" /><path d="M17 11a3 3 0 100-6" /><path d="M2 21v-2a5 5 0 015-5h4a5 5 0 015 5v2" /><path d="M22 21v-2a5 5 0 00-3-4.58" /></svg>;
}
function DownloadIcon() {
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>;
}

/* ============================================================ */
/*  BOOKING SHEET (quote → payment → confirm)                  */
/* ============================================================ */
function BookingSheet({ onClose, onDone, showToast }) {
  const [step, setStep] = useState(0);
  const [pickup, setPickup] = useState("Toronto, ON · M5V 3A8");
  const [dropoff, setDropoff] = useState("Chicago, IL · 60601");
  const [weight, setWeight] = useState("42,000 lb");
  const [trailer, setTrailer] = useState("Dry Van 53'");

  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(0, s - 1));

  return (
    <motion.div
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 280 }}
      style={{ position: "absolute", left: 0, right: 0, top: 60, bottom: 0, background: "rgba(15,15,17,0.98)", backdropFilter: "blur(40px)", borderTopLeftRadius: 28, borderTopRightRadius: 28, color: "white", fontFamily: apple, overflow: "hidden", display: "flex", flexDirection: "column", zIndex: 30 }}
    >
      <div style={{ width: 36, height: 4, background: "rgba(255,255,255,0.3)", borderRadius: 2, margin: "10px auto 8px" }} />
      <div style={{ padding: "0 22px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: ORANGE, fontWeight: 700 }}>STEP {step + 1} OF 4</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, letterSpacing: -0.4 }}>
            {step === 0 && "New Shipment"}
            {step === 1 && "AI Instant Quote"}
            {step === 2 && "Payment"}
            {step === 3 && "Confirmed"}
          </div>
        </div>
        <button onClick={onClose} data-testid="book-close" style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,0.06)", border: "none", color: "white", fontSize: 16, cursor: "pointer" }}>✕</button>
      </div>

      {/* Progress */}
      <div style={{ padding: "0 22px 14px", display: "flex", gap: 3 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ flex: 1, height: 3, background: i <= step ? ORANGE : "rgba(255,255,255,0.1)", borderRadius: 2 }} />)}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 22px 16px" }}>
        {step === 0 && (
          <>
            <BookField label="PICKUP" value={pickup} onChange={setPickup} dot={GREEN} testid="book-pickup" />
            <BookField label="DROP-OFF" value={dropoff} onChange={setDropoff} dot={ORANGE} testid="book-dropoff" />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <BookField label="WEIGHT" value={weight} onChange={setWeight} testid="book-weight" />
              <BookField label="TRAILER" value={trailer} onChange={setTrailer} testid="book-trailer" />
            </div>
            <BookField label="PICKUP WINDOW" value="Tue Jun 24 · 08:00–11:00" testid="book-window" />
          </>
        )}
        {step === 1 && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: ORANGE, fontSize: 11, letterSpacing: 2, fontWeight: 700 }}>
              ✦ AI INSTANT QUOTE
            </div>
            <div style={{ fontSize: 56, fontWeight: 800, letterSpacing: -2, marginTop: 6 }}>$3,150</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.55)", letterSpacing: 1, marginTop: -4 }}>CONFIDENCE 94% · ETA 38h</div>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 14, padding: 14, marginTop: 18 }}>
              {[["Base lane rate", "$2,310"], ["Fuel surcharge", "$420"], ["Trailer (Dry Van)", "$260"], ["Demand × 1.04", "$160"]].map(([l, v]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
                  <span style={{ color: "rgba(255,255,255,0.6)" }}>{l}</span>
                  <span style={{ fontWeight: 500 }}>{v}</span>
                </div>
              ))}
              <div style={{ height: 0.5, background: "rgba(255,255,255,0.1)", margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700 }}>
                <span>Total</span><span style={{ color: ORANGE }}>$3,150</span>
              </div>
            </div>
            <div style={{ marginTop: 12, padding: 12, background: "rgba(16,185,129,0.08)", border: "0.5px solid rgba(16,185,129,0.3)", borderRadius: 12, fontSize: 11, color: "rgba(255,255,255,0.8)" }}>
              ✓ Locks ceiling price. Drivers will reverse-bid down from here.
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <div style={{ background: "linear-gradient(135deg, #2a2a2c, #1a1a1c)", borderRadius: 18, padding: 18, position: "relative", overflow: "hidden" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: "rgba(255,255,255,0.6)" }}>VISA · CREDIT</div>
                <div style={{ fontSize: 12, fontWeight: 700, color: ORANGE, letterSpacing: 2 }}>RY · PAY</div>
              </div>
              <div style={{ fontSize: 18, letterSpacing: 4, marginTop: 38, fontFamily: "monospace" }}>•••• •••• •••• 4148</div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14, fontSize: 11, color: "rgba(255,255,255,0.5)" }}>
                <span>ACME MANUFACTURING</span><span>09/28</span>
              </div>
            </div>
            <div style={{ marginTop: 16, padding: 14, background: "rgba(16,185,129,0.08)", border: "0.5px solid rgba(16,185,129,0.3)", borderRadius: 14 }}>
              <div style={{ fontSize: 10, letterSpacing: 1.5, color: GREEN, fontWeight: 700 }}>ESCROW HOLD</div>
              <div style={{ fontSize: 28, fontWeight: 800, marginTop: 2 }}>$3,150.00</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>Released to driver on POD upload</div>
            </div>
          </>
        )}
        {step === 3 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "30px 10px 10px", textAlign: "center" }}>
            <div style={{ width: 84, height: 84, borderRadius: "50%", background: "rgba(16,185,129,0.18)", border: `2px solid ${GREEN}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="3" strokeLinecap="round"><path d="M5 12l5 5 9-11" /></svg>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginTop: 16, letterSpacing: -0.3 }}>Shipment Booked</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginTop: 10, lineHeight: 1.5, maxWidth: 260 }}>
              Driver bidding is now <strong style={{ color: "white" }}>open</strong>. You&apos;ll get a notification when a carrier wins the load.
            </div>
            <div style={{ marginTop: 22, padding: "10px 14px", background: "rgba(255,107,0,0.1)", border: `0.5px solid ${ORANGE}40`, borderRadius: 12, fontSize: 11, color: "rgba(255,255,255,0.85)", letterSpacing: 0.5 }}>
              Shipment ID · <strong style={{ color: ORANGE }}>RY-78433</strong>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "12px 22px 28px", borderTop: "0.5px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.4)", display: "flex", gap: 10 }}>
        {step > 0 && step < 3 && (
          <button onClick={back} data-testid="book-back" style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "none", padding: "12px 14px", borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: apple, cursor: "pointer" }}>Back</button>
        )}
        <button onClick={step === 3 ? onDone : next} data-testid={`book-next-${step}`} style={{ flex: 2, background: step === 3 ? GREEN : ORANGE, color: "white", border: "none", padding: "12px 14px", borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: apple, cursor: "pointer" }}>
          {step === 0 && "Get AI Quote"}
          {step === 1 && "Accept · Continue to Payment"}
          {step === 2 && "Authorize $3,150 Hold"}
          {step === 3 && "View My Shipments"}
        </button>
      </div>
    </motion.div>
  );
}
function BookField({ label, value, onChange, dot, testid }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return (
    <div style={{ background: "rgba(255,255,255,0.04)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "10px 12px", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9, letterSpacing: 1.5, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>
        {dot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: dot }} />}
        {label}
      </div>
      <input value={v} onChange={(e) => { setV(e.target.value); onChange && onChange(e.target.value); }} data-testid={testid}
        style={{ background: "transparent", border: "none", outline: "none", color: "white", fontSize: 15, fontFamily: apple, width: "100%", marginTop: 2, padding: 0 }} />
    </div>
  );
}

/* ============================================================ */
/*  TRACKING SHEET                                              */
/* ============================================================ */
function TrackingSheet({ load, onClose, showToast }) {
  const steps = ["Booked", "Driver Assigned", "Picked Up", "In Transit", "Delivered"];
  const currentIdx = load.status === "Delivered" ? 4 : load.status === "Pending Pickup" ? 1 : 3;
  return (
    <motion.div
      initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", damping: 30, stiffness: 280 }}
      style={{ position: "absolute", left: 0, right: 0, top: 60, bottom: 0, background: "rgba(15,15,17,0.98)", backdropFilter: "blur(40px)", borderTopLeftRadius: 28, borderTopRightRadius: 28, color: "white", fontFamily: apple, overflow: "hidden", display: "flex", flexDirection: "column", zIndex: 30 }}
    >
      <div style={{ width: 36, height: 4, background: "rgba(255,255,255,0.3)", borderRadius: 2, margin: "10px auto 8px" }} />
      <div style={{ padding: "0 22px 10px", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: ORANGE, fontWeight: 700 }}>{load.id || "RY-78432"}</div>
          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, letterSpacing: -0.4 }}>{load.from} → {load.to}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>{load.trailer} · {load.weight}</div>
        </div>
        <button onClick={onClose} data-testid="track-close" style={{ width: 30, height: 30, borderRadius: "50%", background: "rgba(255,255,255,0.06)", border: "none", color: "white", fontSize: 16, cursor: "pointer" }}>✕</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "0 22px 16px" }}>
        {/* Map preview */}
        <div style={{ position: "relative", height: 160, borderRadius: 14, background: "linear-gradient(135deg, #0e1f1a, #0a141a)", overflow: "hidden", border: "0.5px solid rgba(255,255,255,0.04)" }}>
          <div style={{ position: "absolute", inset: 0, opacity: 0.4, backgroundImage: "linear-gradient(rgba(16,185,129,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(16,185,129,0.06) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
          <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 300 160" preserveAspectRatio="none">
            <path d="M30,120 Q 90,140 150,90 T 270,30" stroke={ORANGE} strokeWidth="2.5" fill="none" strokeDasharray="0" />
          </svg>
          <div style={{ position: "absolute", left: 18, bottom: 22 }}>
            <div style={{ width: 12, height: 12, borderRadius: "50%", background: GREEN, border: "2px solid white", boxShadow: `0 0 0 3px ${GREEN}33` }} />
            <div style={{ fontSize: 11, color: "white", fontWeight: 600, marginTop: 4 }}>{load.from?.split(",")[0]}</div>
          </div>
          <div style={{ position: "absolute", right: 14, top: 14 }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <div style={{ fontSize: 11, color: "white", fontWeight: 600, marginBottom: 2 }}>{load.to?.split(",")[0]}</div>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: ORANGE, border: "2px solid white", boxShadow: `0 0 0 3px ${ORANGE}33` }} />
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div style={{ marginTop: 16, padding: 14, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16 }}>
          <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700, marginBottom: 10 }}>STATUS TIMELINE</div>
          {steps.map((s, i) => {
            const done = i < currentIdx;
            const active = i === currentIdx;
            return (
              <div key={s} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                  <div style={{ width: 22, height: 22, borderRadius: "50%", background: done ? GREEN : active ? ORANGE : "transparent", border: done || active ? "none" : "1.5px solid rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: 11, fontWeight: 700, boxShadow: active ? `0 0 0 4px ${ORANGE}33` : "none" }}>
                    {done ? "✓" : active ? "●" : ""}
                  </div>
                  {i < steps.length - 1 && <div style={{ width: 1.5, height: 26, background: done ? GREEN : "rgba(255,255,255,0.12)", marginTop: 2 }} />}
                </div>
                <div style={{ flex: 1, paddingBottom: i < steps.length - 1 ? 8 : 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: active ? ORANGE : "white" }}>{s}</div>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 1 }}>
                    {done ? "Complete" : active ? "Current" : "Pending"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Driver Card */}
        {currentIdx >= 1 && (
          <div style={{ marginTop: 14, padding: 14, background: "rgba(255,255,255,0.03)", border: "0.5px solid rgba(255,255,255,0.08)", borderRadius: 16 }}>
            <div style={{ fontSize: 11, letterSpacing: 1.5, color: "white", fontWeight: 700, marginBottom: 10 }}>YOUR DRIVER</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: "linear-gradient(135deg, #8b5e3c, #5a3d28)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "white" }}>MT</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Dwayne Johnson</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 2 }}>4.96 ★ · 1.4M mi · 99.2% OTD</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", marginTop: 1 }}>2022 Kenworth T680 · Dry Van 53&apos;</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button onClick={() => showToast("Calling driver…")} data-testid="track-call" style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.15)", padding: "9px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>📞 Call</button>
              <button onClick={() => showToast("Driver chat opened")} data-testid="track-message" style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "0.5px solid rgba(255,255,255,0.15)", padding: "9px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: apple }}>💬 Message</button>
            </div>
          </div>
        )}
      </div>
      <div style={{ padding: "12px 22px 28px", borderTop: "0.5px solid rgba(255,255,255,0.08)", background: "rgba(0,0,0,0.4)", display: "flex", gap: 10 }}>
        <button onClick={() => showToast("Connecting to support…")} style={{ flex: 1, background: "rgba(255,255,255,0.06)", color: "white", border: "none", padding: "12px 14px", borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: apple, cursor: "pointer" }}>Support</button>
        <button onClick={() => showToast("Live map opened")} data-testid="track-livemap" style={{ flex: 2, background: ORANGE, color: "white", border: "none", padding: "12px 14px", borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: apple, cursor: "pointer" }}>Open Live Map</button>
      </div>
    </motion.div>
  );
}
